import { AutoComplete, Field } from '@matillion/component-library'
import React, { useCallback, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { type GitRepositoryDropdownProps } from './types'
import { type BringYourOwnGitDetails } from '../BringYourOwnGitForm/types'
import { useTranslation } from 'react-i18next'
import classes from './GitRepositoryDropdown.module.scss'
import useGetProviderDisplayName from 'modules/BringYourOwnGit/hooks/useGetProviderDisplayName'

const GitRepositoryDropdown = ({
  repositories,
  loading,
  provider,
  className
}: GitRepositoryDropdownProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'byog.form.repository'
  })

  const { providerName } = useGetProviderDisplayName(provider)

  const { errors, handleBlur, touched, values, setFieldValue } =
    useFormikContext<BringYourOwnGitDetails>()

  const handleValueChange = useCallback(
    async (e: { target: { value: { name: string } } }) => {
      const repoFullName = e.target.value.name
      const repositoryDetails = repositories.find(
        (r) => r.fullName === repoFullName
      )

      await setFieldValue('repository', repositoryDetails?.name)

      await setFieldValue('providerParameters', {
        ownerName: repositoryDetails?.owner.name
      })
    },
    [repositories, setFieldValue]
  )

  const availableItems = useMemo(() => {
    return repositories.map((repo) => ({
      id: repo.id,
      name: repo.fullName
    }))
  }, [repositories])

  const errorText = useMemo(() => {
    if (!loading && availableItems.length === 0) {
      return (
        <span data-testid="no-repos-error">
          {t('no-repositories-available.message', { provider: providerName })}
          {t('no-repositories-available.help-prefix')}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://docs.matillion.com/data-productivity-cloud/designer/docs/troubleshooting-github-repository-connection/#unable-to-retrieve-repositories"
          >
            {t('no-repositories-available.help-link')}
          </a>
          {t('no-repositories-available.help-suffix')}
        </span>
      )
    }

    if (touched.repository && errors.repository) {
      return (
        <span data-testid="repository-validation-error">
          {t(errors.repository)}
        </span>
      )
    }

    return null
  }, [
    loading,
    availableItems.length,
    touched.repository,
    errors.repository,
    t,
    providerName
  ])

  const currentItem = useMemo(() => {
    const repository = repositories.find(
      (repo) => repo.name === values.repository
    )

    return {
      id: repository?.id,
      name: repository?.fullName
    }
  }, [repositories, values])

  return (
    <div data-testid="git-repository-selector" className={className}>
      <Field
        required
        loading={loading}
        name="repository"
        title={t('title')}
        onBlur={handleBlur}
        value={currentItem}
        errorText={errorText}
        description={t('subtitle')}
        className={classes.dropdown}
        onChange={handleValueChange}
        inputComponent={AutoComplete}
        placeholder={t('placeholder')}
        availableItems={availableItems}
        data-testid="git-repository-dropdown"
        hasError={!!errors.repository && touched.repository}
      />
    </div>
  )
}

export default GitRepositoryDropdown
